import { COUNTRIES, DEFAULT_COUNTRY } from './identity';
import { WIDGET_CSS } from './styles';
import type { ConnectionState } from './transport';
import type { UiMessage } from './types';

const STATUS_LABEL: Record<ConnectionState, string> = {
  connecting: 'Conectando…',
  connected: 'En línea',
  error: 'Error de conexión',
  closed: 'Desconectado',
};

const CHAT_ICON =
  '<svg viewBox="0 0 24 24"><path d="M20 2H4a2 2 0 0 0-2 2v18l4-4h14a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2z"/></svg>';

/** Ícono de enviar (avión de papel, trazo, hereda currentColor). */
const SEND_ICON =
  '<svg viewBox="0 0 24 24"><path d="M22 2L11 13" stroke-linecap="round" stroke-linejoin="round"/><path d="M22 2l-7 20-4-9-9-4 20-7z" stroke-linecap="round" stroke-linejoin="round"/></svg>';

/** Ícono de clip para adjuntar imagen/documento (trazo, hereda currentColor). */
const ATTACH_ICON =
  '<svg viewBox="0 0 24 24"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" stroke-linecap="round" stroke-linejoin="round"/></svg>';

/** Ícono genérico de documento (hoja con esquina). */
const DOC_ICON =
  '<svg viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke-linecap="round" stroke-linejoin="round"/><path d="M14 2v6h6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
/** Ícono de descarga para el chip de documento. */
const DOWNLOAD_ICON =
  '<svg viewBox="0 0 24 24"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" stroke-linecap="round" stroke-linejoin="round"/><path d="M7 10l5 5 5-5" stroke-linecap="round" stroke-linejoin="round"/><path d="M12 15V3" stroke-linecap="round" stroke-linejoin="round"/></svg>';

/** Ícono de altavoz con sonido activo (avisos sonoros ON). */
const SOUND_ON_ICON =
  '<svg viewBox="0 0 24 24"><path d="M11 5L6 9H2v6h4l5 4V5z" stroke-linecap="round" stroke-linejoin="round"/><path d="M15.5 8.5a5 5 0 0 1 0 7" stroke-linecap="round" stroke-linejoin="round"/><path d="M18.5 5.5a9 9 0 0 1 0 13" stroke-linecap="round" stroke-linejoin="round"/></svg>';
/**
 * Wordmark oficial "dungo" (monolínea) para el footer "Powered by dungo".
 * Paths tomados de dungo-brand/dungo-wordmark.svg con el trazo en currentColor
 * (el bundle del widget no tiene pipeline de assets: va inline).
 */
const DUNGO_WORDMARK =
  '<svg viewBox="0 0 292 100" role="img" aria-label="dungo"><g fill="none" stroke="currentColor" stroke-width="13"><circle cx="30" cy="53" r="16.5"/><path d="M46.5 12 V76"/><path d="M71.5 30 V53 A16.5 16.5 0 0 0 104.5 53 V30"/><path d="M129.5 76 V53 A16.5 16.5 0 0 1 162.5 53 V76"/><circle cx="204" cy="53" r="16.5"/><path d="M220.5 30 V74 A13.5 13.5 0 0 1 193.5 74"/><circle cx="262" cy="53" r="16.5"/></g></svg>';

/** Ícono de altavoz silenciado (avisos sonoros OFF). */
const SOUND_OFF_ICON =
  '<svg viewBox="0 0 24 24"><path d="M11 5L6 9H2v6h4l5 4V5z" stroke-linecap="round" stroke-linejoin="round"/><path d="M22 9l-6 6" stroke-linecap="round" stroke-linejoin="round"/><path d="M16 9l6 6" stroke-linecap="round" stroke-linejoin="round"/></svg>';

/** Clave de preferencia de sonido del widget en localStorage. */
const SOUND_PREF_KEY = 'dungo-widget:sound';
/** Lee la preferencia de sonido (default: activado). Tolera storage no disponible. */
function readSoundPref(): boolean {
  try {
    return localStorage.getItem(SOUND_PREF_KEY) !== 'off';
  } catch {
    return true;
  }
}
/** Persiste la preferencia de sonido. Silencioso si el storage no está disponible. */
function writeSoundPref(enabled: boolean): void {
  try {
    localStorage.setItem(SOUND_PREF_KEY, enabled ? 'on' : 'off');
  } catch {
    /* storage no disponible (modo privado / bloqueado): la preferencia dura la sesión */
  }
}

/** Tamaño máximo de imagen aceptado por el backend (5 MB). */
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
/** Tamaño máximo de documento aceptado por el backend (20 MB). */
const MAX_DOC_BYTES = 20 * 1024 * 1024;

/** `accept` del input: imágenes + documentos permitidos (PDF, Excel, CSV). */
const FILE_ACCEPT =
  'image/*,.pdf,.xlsx,.xls,.csv,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv';

/** MIME types de documento permitidos por chat (PDF, Excel, CSV). */
const DOC_MIME_ALLOW = new Set<string>([
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'text/csv',
]);
/** Extensiones de documento permitidas (fallback con MIME vacío/genérico). */
const DOC_EXT_ALLOW = new Set<string>(['.pdf', '.xlsx', '.xls', '.csv']);
/** Bloqueo explícito de certificados .pfx. */
const PFX_MIME = 'application/x-pkcs12';

/** ¿El archivo es un documento permitido por MIME o (si el MIME es vacío/genérico) por extensión? */
function isAllowedDocument(file: File): boolean {
  const mime = (file.type || '').toLowerCase();
  if (DOC_MIME_ALLOW.has(mime)) return true;
  if (!mime || mime === 'application/octet-stream') {
    const name = (file.name || '').toLowerCase();
    const dot = name.lastIndexOf('.');
    const ext = dot >= 0 ? name.slice(dot) : '';
    return DOC_EXT_ALLOW.has(ext);
  }
  return false;
}

/** Elige el SVG del chip de documento según el MIME (con acento de color por clase). */
export function docIconFor(mime?: string | null): { svg: string; cls: string } {
  const m = (mime || '').toLowerCase();
  if (m === 'application/pdf') return { svg: DOC_ICON, cls: 'doc-pdf' };
  if (
    m === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    m === 'application/vnd.ms-excel'
  ) {
    return { svg: DOC_ICON, cls: 'doc-excel' };
  }
  if (m === 'text/csv') return { svg: DOC_ICON, cls: 'doc-csv' };
  return { svg: DOC_ICON, cls: 'doc-generic' };
}

export interface WidgetUiCallbacks {
  onSend: (text: string) => void;
  /** Envía una imagen con un caption opcional (el texto actual del input). */
  onSendImage: (file: File, caption: string) => void;
  onToggle: (open: boolean) => void;
  /** El contacto tocó "Iniciar Conversación" en el estado idle. */
  onStart: () => void;
  /** MODO PÚBLICO: el visitante envió el formulario de pre-chat (datos ya validados). */
  onPrechatSubmit: (data: PrechatFormData) => void;
  /** MODO PÚBLICO: el visitante tocó "Cambiar mis datos" (re-mostrar el formulario). */
  onChangeData: () => void;
}

/** Datos validados que salen del formulario de pre-chat (modo público). */
export interface PrechatFormData {
  nombre: string;
  apellido?: string;
  email: string;
  /** Prefijo con `+`, ej. `+56`. */
  prefijo: string;
  /** Número local, solo dígitos. */
  numero: string;
}

/** Vista del panel: idle (sin conversación) o active (conversación en curso). */
export type WidgetMode = 'idle' | 'active';

/** Variante de la vista idle: botón "Iniciar Conversación" o formulario de pre-chat. */
type IdleView = 'start' | 'form';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^\d{7,15}$/;

/** Render vanilla del widget aislado en Shadow DOM. */
export class WidgetUI {
  readonly host: HTMLElement;
  private readonly root: ShadowRoot;
  private panel!: HTMLElement;
  private statusEl!: HTMLElement;
  private messagesEl!: HTMLElement;
  private textarea!: HTMLTextAreaElement;
  private sendBtn!: HTMLButtonElement;
  private attachBtn!: HTMLButtonElement;
  private fileInput!: HTMLInputElement;
  private previewEl!: HTMLElement;
  private previewImg!: HTMLImageElement;
  private previewIcon!: HTMLElement;
  private previewName!: HTMLElement;
  private emptyEl!: HTMLElement;
  private composer!: HTMLElement;
  private idleEl!: HTMLElement;
  private startBtn!: HTMLButtonElement;
  private changeLink!: HTMLButtonElement;
  private noticeEl!: HTMLElement;
  private muteBtn!: HTMLButtonElement;
  private prechatEl!: HTMLElement;
  private prechatError!: HTMLElement;
  private prechatBtn!: HTMLButtonElement;
  private inNombre!: HTMLInputElement;
  private inApellido!: HTMLInputElement;
  private inEmail!: HTMLInputElement;
  private selPrefijo!: HTMLSelectElement;
  private inNumero!: HTMLInputElement;
  private open = false;
  private mode: WidgetMode = 'idle';
  /** Variante mostrada cuando el modo es idle (formulario solo en modo público). */
  private idleView: IdleView = 'start';
  /** Preferencia de avisos sonoros del contacto (persistida en localStorage). */
  private soundEnabled = readSoundPref();
  /** Archivo (imagen o documento) seleccionado/pegado pendiente de envío (preview activo). */
  private pendingImage: File | null = null;
  /** Object URL del preview de imagen actual, para revocarlo al limpiar. */
  private pendingPreviewUrl: string | null = null;

  constructor(
    private readonly cb: WidgetUiCallbacks,
    theme: { primaryColor?: string; title?: string },
    private readonly opts: { defaultCountry?: string } = {},
  ) {
    this.host = document.createElement('div');
    this.host.setAttribute('data-dungo-widget', '');
    this.root = this.host.attachShadow({ mode: 'open' });

    const style = document.createElement('style');
    style.textContent = WIDGET_CSS;
    this.root.appendChild(style);

    if (theme.primaryColor) {
      this.host.style.setProperty('--dw-primary', theme.primaryColor);
    }

    this.build(theme.title ?? 'Chat');
    document.body.appendChild(this.host);
  }

  private build(title: string): void {
    const launcher = document.createElement('button');
    launcher.className = 'launcher';
    launcher.setAttribute('aria-label', 'Abrir chat');
    launcher.innerHTML = CHAT_ICON;
    launcher.addEventListener('click', () => this.toggle());

    const panel = document.createElement('div');
    panel.className = 'panel';

    const header = document.createElement('div');
    header.className = 'header';
    const titleWrap = document.createElement('div');
    const titleEl = document.createElement('div');
    titleEl.className = 'title';
    titleEl.textContent = title;
    this.statusEl = document.createElement('div');
    this.statusEl.className = 'status';
    this.statusEl.textContent = STATUS_LABEL.closed;
    titleWrap.append(titleEl, this.statusEl);

    const actions = document.createElement('div');
    actions.className = 'actions';

    this.muteBtn = document.createElement('button');
    this.muteBtn.className = 'mute';
    this.muteBtn.addEventListener('click', () => this.toggleSound());
    this.updateMuteBtn();

    const closeBtn = document.createElement('button');
    closeBtn.className = 'close';
    closeBtn.setAttribute('aria-label', 'Cerrar chat');
    closeBtn.textContent = '×';
    closeBtn.addEventListener('click', () => this.toggle(false));

    actions.append(this.muteBtn, closeBtn);
    header.append(titleWrap, actions);

    // Aviso discreto (p. ej. "La conversación finalizó"). Oculto por defecto.
    this.noticeEl = document.createElement('div');
    this.noticeEl.className = 'notice';
    this.noticeEl.hidden = true;

    this.messagesEl = document.createElement('div');
    this.messagesEl.className = 'messages';
    this.emptyEl = document.createElement('div');
    this.emptyEl.className = 'empty';
    this.emptyEl.textContent = 'Escríbenos y te respondemos aquí mismo.';
    this.messagesEl.appendChild(this.emptyEl);

    // Vista idle: sin conversación activa, solo el botón central para iniciar.
    this.idleEl = document.createElement('div');
    this.idleEl.className = 'idle';
    const idleHint = document.createElement('div');
    idleHint.className = 'idle-hint';
    idleHint.textContent = '¿Necesitas ayuda? Comienza una conversación con nosotros.';
    this.startBtn = document.createElement('button');
    this.startBtn.className = 'start';
    this.startBtn.textContent = 'Iniciar Conversación';
    this.startBtn.addEventListener('click', () => this.cb.onStart());
    // Link discreto para editar los datos guardados (solo se muestra en modo público).
    this.changeLink = document.createElement('button');
    this.changeLink.className = 'link-btn';
    this.changeLink.type = 'button';
    this.changeLink.textContent = 'Cambiar mis datos';
    this.changeLink.hidden = true;
    this.changeLink.addEventListener('click', () => this.cb.onChangeData());
    this.idleEl.append(idleHint, this.startBtn, this.changeLink);

    // Formulario de pre-chat (modo público). Oculto hasta que showPrechat() lo pida.
    this.prechatEl = this.buildPrechat();
    this.prechatEl.hidden = true;

    this.composer = document.createElement('div');
    this.composer.className = 'composer';

    // Input de archivo oculto, disparado por el botón de adjuntar.
    this.fileInput = document.createElement('input');
    this.fileInput.type = 'file';
    this.fileInput.accept = FILE_ACCEPT;
    this.fileInput.hidden = true;
    this.fileInput.addEventListener('change', () => {
      const file = this.fileInput.files?.[0];
      if (file) this.selectFile(file);
      // Reset para permitir re-seleccionar el mismo archivo.
      this.fileInput.value = '';
    });

    // Preview de la imagen adjunta (thumbnail + nombre + quitar). Oculto por defecto.
    this.previewEl = document.createElement('div');
    this.previewEl.className = 'attach-preview';
    this.previewEl.hidden = true;
    this.previewImg = document.createElement('img');
    this.previewImg.alt = 'Imagen adjunta';
    this.previewImg.hidden = true;
    this.previewIcon = document.createElement('span');
    this.previewIcon.className = 'doc-icon';
    this.previewIcon.hidden = true;
    this.previewName = document.createElement('span');
    this.previewName.className = 'name';
    const removeBtn = document.createElement('button');
    removeBtn.className = 'remove';
    removeBtn.type = 'button';
    removeBtn.setAttribute('aria-label', 'Quitar adjunto');
    removeBtn.textContent = '×';
    removeBtn.addEventListener('click', () => this.clearPendingFile());
    this.previewEl.append(this.previewImg, this.previewIcon, this.previewName, removeBtn);

    const row = document.createElement('div');
    row.className = 'row';

    this.attachBtn = document.createElement('button');
    this.attachBtn.className = 'attach';
    this.attachBtn.type = 'button';
    this.attachBtn.setAttribute('aria-label', 'Adjuntar imagen o documento');
    this.attachBtn.innerHTML = ATTACH_ICON;
    this.attachBtn.addEventListener('click', () => this.fileInput.click());

    this.textarea = document.createElement('textarea');
    this.textarea.rows = 1;
    this.textarea.placeholder = 'Escribe un mensaje…';
    this.textarea.addEventListener('input', () => this.autoGrow());
    this.textarea.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        this.submit();
      }
    });
    // Pegar una imagen desde el portapapeles la adjunta como si se eligiera.
    this.textarea.addEventListener('paste', (e) => this.handlePaste(e));

    this.sendBtn = document.createElement('button');
    this.sendBtn.className = 'send';
    this.sendBtn.setAttribute('aria-label', 'Enviar');
    this.sendBtn.setAttribute('title', 'Enviar');
    this.sendBtn.innerHTML = SEND_ICON;
    this.sendBtn.addEventListener('click', () => this.submit());

    row.append(this.attachBtn, this.textarea, this.sendBtn);
    this.composer.append(this.fileInput, this.previewEl, row);

    // Footer permanente "Powered by dungo" (todas las vistas y modos).
    const powered = document.createElement('a');
    powered.className = 'powered';
    powered.href = 'https://dungo.ai';
    powered.target = '_blank';
    powered.rel = 'noopener noreferrer';
    powered.setAttribute('aria-label', 'Powered by dungo');
    powered.innerHTML = `Powered by ${DUNGO_WORDMARK}`;

    panel.append(header, this.noticeEl, this.messagesEl, this.idleEl, this.prechatEl, this.composer, powered);
    this.panel = panel;
    this.root.append(launcher, panel);

    this.setMode('idle');
  }

  /** Construye la card del formulario de pre-chat (modo público). */
  private buildPrechat(): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'prechat';

    const card = document.createElement('form');
    card.className = 'card';
    card.noValidate = true;
    card.addEventListener('submit', (e) => {
      e.preventDefault();
      this.submitPrechat();
    });

    const title = document.createElement('p');
    title.className = 'prechat-title';
    title.textContent = 'Cuéntanos quién eres para empezar:';

    const row2 = document.createElement('div');
    row2.className = 'row2';
    this.inNombre = document.createElement('input');
    this.inNombre.type = 'text';
    this.inNombre.placeholder = 'Nombre';
    this.inNombre.autocomplete = 'given-name';
    this.inNombre.maxLength = 100;
    this.inApellido = document.createElement('input');
    this.inApellido.type = 'text';
    this.inApellido.placeholder = 'Apellido';
    this.inApellido.autocomplete = 'family-name';
    this.inApellido.maxLength = 100;
    row2.append(this.inNombre, this.inApellido);

    this.inEmail = document.createElement('input');
    this.inEmail.type = 'email';
    this.inEmail.placeholder = 'Correo';
    this.inEmail.autocomplete = 'email';
    this.inEmail.maxLength = 255;

    const phoneRow = document.createElement('div');
    phoneRow.className = 'phone-row';
    this.selPrefijo = document.createElement('select');
    this.selPrefijo.className = 'prefix';
    this.selPrefijo.setAttribute('aria-label', 'Código de país');
    const defaultCountry = this.opts.defaultCountry ?? DEFAULT_COUNTRY;
    for (const c of COUNTRIES) {
      const opt = document.createElement('option');
      opt.value = c.prefix;
      opt.textContent = `${c.flag} ${c.prefix}`;
      if (c.code === defaultCountry) opt.selected = true;
      this.selPrefijo.appendChild(opt);
    }
    this.inNumero = document.createElement('input');
    this.inNumero.type = 'tel';
    this.inNumero.placeholder = 'Teléfono';
    this.inNumero.autocomplete = 'tel-national';
    this.inNumero.maxLength = 20;
    phoneRow.append(this.selPrefijo, this.inNumero);

    this.prechatError = document.createElement('p');
    this.prechatError.className = 'prechat-error';
    this.prechatError.hidden = true;

    this.prechatBtn = document.createElement('button');
    this.prechatBtn.className = 'submit';
    this.prechatBtn.type = 'submit';
    this.prechatBtn.textContent = 'Iniciar chat';

    card.append(title, row2, this.inEmail, phoneRow, this.prechatError, this.prechatBtn);
    wrap.appendChild(card);
    return wrap;
  }

  /** Valida el formulario de pre-chat y, si está OK, dispara el callback. */
  private submitPrechat(): void {
    const nombre = this.inNombre.value.trim();
    const apellido = this.inApellido.value.trim();
    const email = this.inEmail.value.trim();
    const prefijo = this.selPrefijo.value;
    const numero = this.inNumero.value.replace(/[\s\-().]/g, '');

    this.inNombre.classList.toggle('invalid', !nombre);
    const emailOk = EMAIL_RE.test(email);
    this.inEmail.classList.toggle('invalid', !emailOk);
    const numeroOk = PHONE_RE.test(numero);
    this.inNumero.classList.toggle('invalid', !numeroOk);

    let error: string | null = null;
    if (!nombre) error = 'Ingresa tu nombre.';
    else if (!emailOk) error = 'Ingresa un correo válido.';
    else if (!numeroOk) error = 'Ingresa un teléfono válido (solo números).';

    this.prechatError.textContent = error ?? '';
    this.prechatError.hidden = !error;
    if (error) return;

    this.cb.onPrechatSubmit({
      nombre,
      apellido: apellido || undefined,
      email,
      prefijo,
      numero,
    });
  }

  private autoGrow(): void {
    this.textarea.style.height = 'auto';
    this.textarea.style.height = `${Math.min(this.textarea.scrollHeight, 96)}px`;
  }

  private submit(): void {
    const text = this.textarea.value.trim();
    // Si hay archivo adjunto (imagen o documento), prima el envío de media (con el texto como caption).
    if (this.pendingImage) {
      const file = this.pendingImage;
      const caption = text;
      this.textarea.value = '';
      this.autoGrow();
      this.clearPendingFile();
      this.cb.onSendImage(file, caption);
      return;
    }
    if (!text) return;
    this.textarea.value = '';
    this.autoGrow();
    this.cb.onSend(text);
  }

  /** Toma una imagen del portapapeles si la hay; el resto del paste sigue normal. Solo imágenes por paste. */
  private handlePaste(e: ClipboardEvent): void {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.kind === 'file' && item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) {
          e.preventDefault();
          this.selectFile(file);
        }
        return;
      }
    }
  }

  /** Valida y fija el archivo pendiente (imagen o documento), mostrando su preview. */
  private selectFile(file: File): void {
    const mime = (file.type || '').toLowerCase();
    const name = (file.name || '').toLowerCase();

    // Bloqueo explícito de .pfx.
    if (mime === PFX_MIME || name.endsWith('.pfx')) {
      this.showNotice('Tipo de archivo no permitido.');
      return;
    }

    const isImg = mime.startsWith('image/');
    const isDoc = !isImg && isAllowedDocument(file);

    if (!isImg && !isDoc) {
      this.showNotice('Tipo de archivo no permitido.');
      return;
    }

    const max = isImg ? MAX_IMAGE_BYTES : MAX_DOC_BYTES;
    if (file.size > max) {
      this.showNotice(isImg ? 'La imagen supera el máximo de 5 MB.' : 'El documento supera el máximo de 20 MB.');
      return;
    }

    this.clearPendingFile();
    this.pendingImage = file;
    if (isImg) {
      this.pendingPreviewUrl = URL.createObjectURL(file);
      this.previewImg.src = this.pendingPreviewUrl;
      this.previewImg.hidden = false;
      this.previewIcon.hidden = true;
    } else {
      const icon = docIconFor(mime);
      this.previewIcon.innerHTML = icon.svg;
      this.previewIcon.className = `doc-icon ${icon.cls}`;
      this.previewIcon.hidden = false;
      this.previewImg.hidden = true;
    }
    this.previewName.textContent = file.name || (isDoc ? 'documento' : 'imagen');
    this.previewEl.hidden = false;
    this.textarea.focus();
  }

  /** Limpia el archivo pendiente y libera su object URL. */
  private clearPendingFile(): void {
    this.pendingImage = null;
    if (this.pendingPreviewUrl) {
      URL.revokeObjectURL(this.pendingPreviewUrl);
      this.pendingPreviewUrl = null;
    }
    this.previewImg.removeAttribute('src');
    this.previewImg.hidden = true;
    this.previewIcon.innerHTML = '';
    this.previewIcon.hidden = true;
    this.previewName.textContent = '';
    this.previewEl.hidden = true;
  }

  toggle(force?: boolean): void {
    this.open = force ?? !this.open;
    this.panel.classList.toggle('open', this.open);
    this.cb.onToggle(this.open);
    if (this.open && this.mode === 'active') {
      this.textarea.focus();
      this.scrollToBottom();
    }
  }

  isOpen(): boolean {
    return this.open;
  }

  getMode(): WidgetMode {
    return this.mode;
  }

  /**
   * Aplica el color primario en runtime (config remota del channel). El color
   * del host pasado en `theme.primaryColor` se setea en el constructor y el
   * widget NO llama esto cuando existe, así que siempre gana el del host.
   */
  setPrimaryColor(color: string): void {
    this.host.style.setProperty('--dw-primary', color);
  }

  /** ¿Están activados los avisos sonoros? (lo consulta el widget antes de sonar). */
  isSoundEnabled(): boolean {
    return this.soundEnabled;
  }

  /** Alterna los avisos sonoros, persiste la preferencia y refresca el botón. */
  private toggleSound(): void {
    this.soundEnabled = !this.soundEnabled;
    writeSoundPref(this.soundEnabled);
    this.updateMuteBtn();
  }

  /** Refleja el estado de sonido en el botón del header (ícono, título, aria). */
  private updateMuteBtn(): void {
    if (!this.muteBtn) return;
    const on = this.soundEnabled;
    this.muteBtn.innerHTML = on ? SOUND_ON_ICON : SOUND_OFF_ICON;
    this.muteBtn.classList.toggle('muted', !on);
    const label = on ? 'Silenciar avisos de sonido' : 'Activar avisos de sonido';
    this.muteBtn.setAttribute('aria-label', label);
    this.muteBtn.setAttribute('title', label);
    this.muteBtn.setAttribute('aria-pressed', String(!on));
  }

  /**
   * Cambia entre idle (botón "Iniciar Conversación") y active (mensajes + input).
   * En idle se ocultan mensajes y composer; en active se muestran.
   */
  setMode(mode: WidgetMode): void {
    this.mode = mode;
    const active = mode === 'active';
    if (!active) this.clearPendingFile();
    this.messagesEl.hidden = !active;
    this.composer.hidden = !active;
    this.applyIdleView();
    if (active && this.open) {
      this.textarea.focus();
      this.scrollToBottom();
    }
  }

  /** Refleja qué vista idle corresponde (botón o formulario) según modo y variante. */
  private applyIdleView(): void {
    const active = this.mode === 'active';
    this.idleEl.hidden = active || this.idleView === 'form';
    this.prechatEl.hidden = active || this.idleView !== 'form';
  }

  /**
   * MODO PÚBLICO: muestra el formulario de pre-chat en la vista idle,
   * opcionalmente prellenado (visitante que edita sus datos).
   */
  showPrechat(prefill?: Partial<PrechatFormData>): void {
    this.idleView = 'form';
    if (prefill) {
      this.inNombre.value = prefill.nombre ?? '';
      this.inApellido.value = prefill.apellido ?? '';
      this.inEmail.value = prefill.email ?? '';
      if (prefill.prefijo) this.selPrefijo.value = prefill.prefijo;
      this.inNumero.value = prefill.numero ?? '';
    }
    this.prechatError.hidden = true;
    this.applyIdleView();
    if (this.open && this.mode === 'idle') this.inNombre.focus();
  }

  /**
   * MODO PÚBLICO / embebido: muestra la vista idle con el botón de iniciar.
   * `showChangeLink` agrega el link "Cambiar mis datos" (identidad guardada).
   */
  showIdleStart(showChangeLink = false): void {
    this.idleView = 'start';
    this.changeLink.hidden = !showChangeLink;
    this.applyIdleView();
  }

  /** Estado ocupado del formulario de pre-chat mientras se mintea/conecta. */
  setPrechatSubmitting(submitting: boolean): void {
    for (const el of [this.inNombre, this.inApellido, this.inEmail, this.selPrefijo, this.inNumero]) {
      el.disabled = submitting;
    }
    this.prechatBtn.disabled = submitting;
    this.prechatBtn.textContent = submitting ? 'Conectando…' : 'Iniciar chat';
  }

  /** Limpia los mensajes pintados (al volver a idle / iniciar conversación nueva). */
  clearMessages(): void {
    this.messagesEl.replaceChildren(this.emptyEl);
  }

  /** Estado ocupado del botón "Iniciar Conversación" mientras se mintea/conecta. */
  setStarting(starting: boolean): void {
    this.startBtn.disabled = starting;
    this.startBtn.textContent = starting ? 'Conectando…' : 'Iniciar Conversación';
  }

  /** Muestra un aviso discreto en el panel. `clearNotice()` lo oculta. */
  showNotice(text: string): void {
    this.noticeEl.textContent = text;
    this.noticeEl.hidden = false;
  }

  clearNotice(): void {
    this.noticeEl.hidden = true;
    this.noticeEl.textContent = '';
  }

  setState(state: ConnectionState): void {
    this.statusEl.textContent = STATUS_LABEL[state];
  }

  setSending(sending: boolean): void {
    this.sendBtn.disabled = sending;
    this.attachBtn.disabled = sending;
  }

  /** Renderiza un mensaje y devuelve su nodo (para actualizar estado de envío). */
  addMessage(msg: UiMessage): HTMLElement {
    this.emptyEl.remove();
    const isImage = !!msg.imageUrl;
    const isDoc = !isImage && !!msg.docUrl;
    const isMedia = isImage || isDoc;
    const el = document.createElement('div');
    el.className =
      `msg ${msg.direction}` + (isMedia ? ' media' : '') + (msg.status === 'error' ? ' error' : '');
    if (msg.direction === 'in' && msg.name) {
      const who = document.createElement('span');
      who.className = 'who';
      who.textContent = msg.name;
      el.appendChild(who);
    }
    if (isImage) {
      const img = document.createElement('img');
      img.className = 'img';
      img.src = msg.imageUrl!;
      img.alt = msg.caption || 'Imagen';
      img.loading = 'lazy';
      // Click -> abrir la imagen en una pestaña nueva (vista grande).
      img.addEventListener('click', () => {
        window.open(msg.imageUrl!, '_blank', 'noopener,noreferrer');
      });
      el.appendChild(img);
      if (msg.caption) {
        const cap = document.createElement('span');
        cap.className = 'caption';
        cap.textContent = msg.caption;
        el.appendChild(cap);
      }
    } else if (isDoc) {
      const icon = docIconFor(msg.docMime);
      const link = document.createElement('a');
      link.className = 'doc';
      link.href = msg.docUrl!;
      link.setAttribute('download', msg.docName || '');
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      const iconEl = document.createElement('span');
      iconEl.className = `doc-icon ${icon.cls}`;
      iconEl.innerHTML = icon.svg;
      const nameEl = document.createElement('span');
      nameEl.className = 'doc-name';
      nameEl.textContent = msg.docName || 'Documento';
      const dl = document.createElement('span');
      dl.className = 'doc-dl';
      dl.innerHTML = DOWNLOAD_ICON;
      link.append(iconEl, nameEl, dl);
      el.appendChild(link);
      if (msg.caption) {
        const cap = document.createElement('span');
        cap.className = 'caption';
        cap.textContent = msg.caption;
        el.appendChild(cap);
      }
    } else {
      el.appendChild(document.createTextNode(msg.text));
    }
    const meta = document.createElement('span');
    meta.className = 'meta';
    meta.textContent = this.formatTime(msg.timestamp);
    el.appendChild(meta);
    this.messagesEl.appendChild(el);
    this.scrollToBottom();
    return el;
  }

  markError(el: HTMLElement): void {
    el.classList.add('error');
  }

  private formatTime(ts: string): string {
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  private scrollToBottom(): void {
    this.messagesEl.scrollTop = this.messagesEl.scrollHeight;
  }

  destroy(): void {
    this.clearPendingFile();
    this.host.remove();
  }
}
